import _get from 'lodash/get';
import { resourceFields } from './resourceFields';
import { SupportedResourceTypes, DataSourceType } from './types';

export type ResourceDateFilter = {
  /**
   * The property name to query by via the fhir spec
   */
  property: string;
  /**
   * The display name for the user selectable configuration
   */
  displayName: string;
  /**
   * The property path/selector within the resource to use for the fhir datetime value
   */
  dateSelector?: string;
  /**
   * The property path/selector within the resource to use for the fhir period value
   */
  periodSelector?: string;
};

export type DateFilterPropertyHash = {
  [key in SupportedResourceTypes]: ResourceDateFilter[];
};

const dateFilterProperties: DateFilterPropertyHash = {
  Observation: [
    {
      property: 'date',
      displayName: 'Observed Date',
      dateSelector: 'effectiveDateTime',
      periodSelector: 'effectivePeriod',
    },
  ],
  MedicationStatement: [
    {
      property: 'effective',
      displayName: 'Effective Date',
      dateSelector: 'effectiveDateTime',
      periodSelector: 'effectivePeriod',
    },
  ],
  MedicationAdministration: [
    {
      property: 'effective-time',
      displayName: 'Effective Date',
      dateSelector: 'effectiveDateTime',
      periodSelector: 'effectivePeriod',
    },
  ],
  MedicationRequest: [
    {
      property: 'authoredon',
      displayName: 'Authored On',
      dateSelector: 'authoredOn',
    },
  ],
  MedicationDispense: [
    {
      property: 'whenhandedover',
      displayName: 'When Handed Over',
      dateSelector: 'whenHandedOver',
    },
  ],
  Immunization: [
    {
      property: 'date',
      displayName: 'Immunization Date',
      dateSelector: 'date',
    },
  ],
  Procedure: [
    {
      property: 'date',
      displayName: 'Procedure Date',
      dateSelector: 'performedDateTime',
      periodSelector: 'performedPeriod',
    },
  ],
  Encounter: [
    {
      property: 'date',
      displayName: 'Encounter Date',
      periodSelector: 'period',
    },
  ],
  Condition: [
    {
      property: 'onset-date',
      displayName: 'Onset Date',
      dateSelector: 'onsetDateTime',
      periodSelector: 'onsetPeriod',
    },
    {
      property: 'abatement-date',
      displayName: 'Abatement Date',
      dateSelector: 'abatementDateTime',
      periodSelector: 'abatementPeriod',
    },
  ],
  Goal: [],
  Sequence: [],
  Media: [
    {
      property: 'date',
      displayName: 'Collected Date',
      dateSelector: 'content.creation',
    },
    {
      property: 'created',
      displayName: 'Created Date',
      dateSelector: 'occurenceDateTime',
      periodSelector: 'occurencePeriod',
    },
  ],
  ProcedureRequest: [],
  DocumentReference: [
    {
      property: 'created',
      displayName: 'Created Date',
      dateSelector: 'created',
    },
    {
      property: 'period',
      displayName: 'Time of Service Being Documented',
      periodSelector: 'context.period',
    },
  ],
  DiagnosticReport: [
    {
      property: 'date',
      displayName: 'Report Date',
      dateSelector: 'effectiveDateTime',
      periodSelector: 'effectivePeriod',
    },
  ],
  ReferralRequest: [
    {
      property: 'authored-on',
      displayName: 'Create Date',
      dateSelector: 'authoredOn',
    },
  ],
  Specimen: [
    {
      property: 'collected',
      displayName: 'Collection Date',
      dateSelector: 'collection.collectedDateTime',
      periodSelector: 'collection.collectedPeriod',
    },
  ],
  ImagingStudy: [
    {
      property: 'started',
      displayName: 'Study Start Date',
      dateSelector: 'started',
    },
  ],
  Consent: [
    {
      property: 'date',
      displayName: 'Date',
      dateSelector: 'dateTime',
      periodSelector: 'period',
    },
  ],
  Coverage: [
    {
      property: 'date',
      displayName: 'Date',
      dateSelector: 'dateTime',
      periodSelector: 'period',
    },
  ],
  ExplanationOfBenefit: [
    {
      property: 'date',
      displayName: 'Date',
      dateSelector: 'dateTime',
      periodSelector: 'period',
    },
  ],
};

export type DateOption = {
  value: string;
  display: string;
};

export type ResourceTypDateOptionsHash = {
  [key in SupportedResourceTypes]: DateOption[];
};

// pre-calculate a selection control version of config.  This prevents re-constructing every time and forcing downstream re-renders
const preCalculateDateFilterSelectOptions = (): ResourceTypDateOptionsHash => {
  const resourceTypes = Object.keys(
    dateFilterProperties,
  ) as SupportedResourceTypes[];
  const resourceSelections = {} as ResourceTypDateOptionsHash;
  resourceTypes.forEach(resourceType => {
    const dateFilterOptions = dateFilterProperties[resourceType];
    const selectOptions = dateFilterOptions.map(option => ({
      value: option.property,
      display: option.displayName,
    }));

    resourceSelections[resourceType] = selectOptions;
  });

  return resourceSelections;
};

const resourceTypeToSelectOpts = preCalculateDateFilterSelectOptions();

export type FhirResourceConfig = {
  resource: SupportedResourceTypes;
  displayName: string;
  dateFilterProperties: ResourceDateFilter[];
  dateFilterSelectOptions: DateOption[];
  fields: string[];
};

export type DataSourceTypeConfig = {
  id: DataSourceType;
  canQueryByCategory: boolean;
  fhirResource: FhirResourceConfig[];
};

export type DataSourceTypes = {
  [key: string]: DataSourceTypeConfig;
};

const dataSourceTypes: DataSourceTypes = {
  observations: {
    id: 'OBSERVATION_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'Observation',
        displayName: 'Observation',
        dateFilterProperties: dateFilterProperties['Observation'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Observation'],
        fields: [
          resourceFields.Observation.code,
          resourceFields.Observation.valueCodeableConcept,
        ],
      },
    ],
  },
  goals: {
    id: 'GOAL_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'Goal',
        displayName: 'Goal',
        dateFilterProperties: dateFilterProperties['Goal'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Goal'],
        fields: [resourceFields.Goal.target],
      },
    ],
  },
  medication: {
    id: 'MEDICATION_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'MedicationStatement',
        displayName: 'Medication Statement',
        dateFilterProperties: dateFilterProperties['MedicationStatement'],
        dateFilterSelectOptions:
          resourceTypeToSelectOpts['MedicationStatement'],
        fields: [
          resourceFields.MedicationStatement.medicationCodeableConcept,
          resourceFields.MedicationStatement.contained,
        ],
      },
      {
        resource: 'MedicationAdministration',
        displayName: 'Medication Administration',
        dateFilterProperties: dateFilterProperties['MedicationAdministration'],
        dateFilterSelectOptions:
          resourceTypeToSelectOpts['MedicationAdministration'],
        fields: [
          resourceFields.MedicationAdministration.medicationCodeableConcept,
          resourceFields.MedicationAdministration.contained,
        ],
      },
      {
        resource: 'MedicationRequest',
        displayName: 'Medication Request',
        dateFilterProperties: dateFilterProperties['MedicationRequest'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['MedicationRequest'],
        fields: [
          resourceFields.MedicationRequest.medicationCodeableConcept,
          resourceFields.MedicationRequest.contained,
        ],
      },
      {
        resource: 'MedicationDispense',
        displayName: 'Medication Dispense',
        dateFilterProperties: dateFilterProperties['MedicationDispense'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['MedicationDispense'],
        fields: [
          resourceFields.MedicationDispense.medicationCodeableConcept,
          resourceFields.MedicationRequest.contained,
        ],
      },
    ],
  },
  medicationRequest: {
    id: 'MEDICATION_REQUEST_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'MedicationRequest',
        displayName: 'Medication Request',
        dateFilterProperties: dateFilterProperties['MedicationRequest'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['MedicationRequest'],
        fields: [
          resourceFields.MedicationRequest.medicationCodeableConcept,
          resourceFields.MedicationRequest.contained,
        ],
      },
    ],
  },
  medicationAdministration: {
    id: 'MEDICATION_ADMINISTRATION_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'MedicationAdministration',
        displayName: 'Medication Administration',
        dateFilterProperties: dateFilterProperties['MedicationAdministration'],
        dateFilterSelectOptions:
          resourceTypeToSelectOpts['MedicationAdministration'],
        fields: [
          resourceFields.MedicationAdministration.medicationCodeableConcept,
          resourceFields.MedicationAdministration.contained,
        ],
      },
    ],
  },
  medicationStatement: {
    id: 'MEDICATION_STATEMENT_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'MedicationStatement',
        displayName: 'Medication Statement',
        dateFilterProperties: dateFilterProperties['MedicationStatement'],
        dateFilterSelectOptions:
          resourceTypeToSelectOpts['MedicationStatement'],
        fields: [
          resourceFields.MedicationStatement.medicationCodeableConcept,
          resourceFields.MedicationStatement.contained,
        ],
      },
    ],
  },
  medicationImmunization: {
    id: 'MEDICATION_IMMUNIZATION_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Immunization',
        displayName: 'Immunization',
        dateFilterProperties: dateFilterProperties['Immunization'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Immunization'],
        fields: [resourceFields.Immunization.vaccineCode],
      },
    ],
  },
  medicationDispense: {
    id: 'MEDICATION_DISPENSE_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'MedicationDispense',
        displayName: 'Medication Dispense',
        dateFilterProperties: dateFilterProperties['MedicationDispense'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['MedicationDispense'],
        fields: [
          resourceFields.MedicationDispense.medicationCodeableConcept,
          resourceFields.MedicationDispense.contained,
        ],
      },
    ],
  },
  procedures: {
    id: 'PROCEDURE_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'Procedure',
        displayName: 'Procedure',
        dateFilterProperties: dateFilterProperties['Procedure'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Procedure'],
        fields: [resourceFields.Procedure.code],
      },
    ],
  },
  encounters: {
    id: 'ENCOUNTER_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Encounter',
        displayName: 'Encounter',
        dateFilterProperties: dateFilterProperties['Encounter'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Encounter'],
        fields: [resourceFields.Encounter.type],
      },
    ],
  },
  conditions: {
    id: 'CONDITION_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'Condition',
        displayName: 'Condition',
        dateFilterProperties: dateFilterProperties['Condition'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Condition'],
        fields: [resourceFields.Condition.code],
      },
    ],
  },
  sequencingEvents: {
    id: 'SEQUENCING_EVENT_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Sequence',
        displayName: 'Sequence',
        dateFilterProperties: dateFilterProperties['Sequence'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Sequence'],
        fields: [
          resourceFields.Sequence.chromosome,
          resourceFields.Sequence.type,
        ],
      },
    ],
  },
  media: {
    id: 'MEDIA_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Media',
        displayName: 'Media',
        dateFilterProperties: dateFilterProperties['Media'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Media'],
        fields: [],
      },
    ],
  },
  documentReference: {
    id: 'DOCUMENT_REFERENCE_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'DocumentReference',
        displayName: 'Document Reference',
        dateFilterProperties: dateFilterProperties['DocumentReference'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['DocumentReference'],
        fields: [resourceFields.DocumentReference.typeCoding],
      },
    ],
  },
  diagnosticReport: {
    id: 'DIAGNOSTIC_REPORT_DATA_SOURCE_TYPE',
    canQueryByCategory: true,
    fhirResource: [
      {
        resource: 'DiagnosticReport',
        displayName: 'Diagnostic Report',
        dateFilterProperties: dateFilterProperties['DiagnosticReport'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['DiagnosticReport'],
        fields: [],
      },
    ],
  },
  referralRequest: {
    id: 'REFERRAL_REQUEST_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'ReferralRequest',
        displayName: 'Referral Request',
        dateFilterProperties: dateFilterProperties['ReferralRequest'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['ReferralRequest'],
        fields: [],
      },
    ],
  },
  specimen: {
    id: 'SPECIMEN_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Specimen',
        displayName: 'Specimen',
        dateFilterProperties: dateFilterProperties['Specimen'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Specimen'],
        fields: [],
      },
    ],
  },
  imagingStudy: {
    id: 'IMAGING_STUDY_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'ImagingStudy',
        displayName: 'Imaging Study',
        dateFilterProperties: dateFilterProperties['ImagingStudy'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['ImagingStudy'],
        fields: [],
      },
    ],
  },
  consent: {
    id: 'CONSENT_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Consent',
        displayName: 'Consent',
        dateFilterProperties: dateFilterProperties['Consent'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Consent'],
        fields: [],
      },
    ],
  },
  procedureRequest: {
    id: 'PROCEDURE_REQUEST_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'ProcedureRequest',
        displayName: 'Procedure Request',
        dateFilterProperties: dateFilterProperties['ProcedureRequest'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['ProcedureRequest'],
        fields: [],
      },
    ],
  },
  coverage: {
    id: 'COVERAGE_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'Coverage',
        displayName: 'Coverage',
        dateFilterProperties: dateFilterProperties['Coverage'],
        dateFilterSelectOptions: resourceTypeToSelectOpts['Coverage'],
        fields: [],
      },
    ],
  },
  explanationOfBenefit: {
    id: 'EOB_DATA_SOURCE_TYPE',
    canQueryByCategory: false,
    fhirResource: [
      {
        resource: 'ExplanationOfBenefit',
        displayName: 'ExplanationOfBenefit',
        dateFilterProperties: dateFilterProperties['ExplanationOfBenefit'],
        dateFilterSelectOptions:
          resourceTypeToSelectOpts['ExplanationOfBenefit'],
        fields: [],
      },
    ],
  },
};

export const idToDataSourceType = (id: DataSourceType) => {
  const key = Object.keys(dataSourceTypes).find(key => {
    const dataSourceTypeConfig = dataSourceTypes[key];
    const dataSourceTypeId = _get<DataSourceTypeConfig, 'id', DataSourceType>(
      dataSourceTypeConfig,
      'id',
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      null,
    );
    return dataSourceTypeId === id;
  });

  // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
  return _get(dataSourceTypes, key);
};

export const idToFhirResources = (id: DataSourceType): FhirResourceConfig[] => {
  const dataSourceType = idToDataSourceType(id);
  return _get(dataSourceType, 'fhirResource', []);
};

export const idToFhirResourceTypes = (id: DataSourceType) =>
  idToFhirResources(id).map(fhirResource => fhirResource.resource);

export const idToFhirResourceFields = (id: DataSourceType) =>
  idToFhirResources(id).reduce(
    (prev: string[], fhirResource) => [...prev, ...(fhirResource.fields || [])],
    [],
  );

export type ResourceTypeDefaultTimeFilterHash = {
  [key in SupportedResourceTypes]: string;
};

export const idToDefaultTimeFilters = (
  id: DataSourceType,
): ResourceTypeDefaultTimeFilterHash => {
  const fhirResources = idToFhirResources(id);
  return fhirResources.reduce(
    (acc: ResourceTypeDefaultTimeFilterHash, resource: FhirResourceConfig) => {
      const defaultDateFilterProperty = _get(
        resource,
        'dateFilterProperties[0].property',
      ) as string;
      if (!defaultDateFilterProperty) return acc;

      return {
        ...acc,
        [resource.resource]: defaultDateFilterProperty,
      };
    },
    {} as ResourceTypeDefaultTimeFilterHash,
  );
};

/**
 * Returns the date filter properties given the fhir resource type and
 * optionally configured and user-specified date filter property.
 *
 * If no configured date filter property is provided, the default date filter is returned
 */
export const fhirResourceToDateFilter = (
  fhirResource: SupportedResourceTypes,
  configuredDateFilterProperty: string | null,
): ResourceDateFilter | null => {
  const dateFiltersForResourceType = dateFilterProperties[fhirResource] || [];
  if (!dateFiltersForResourceType.length) return null;

  // default to the first option
  const defaultDateFilter = dateFiltersForResourceType[0] || null;

  if (!configuredDateFilterProperty) {
    return defaultDateFilter;
  }

  const configuredDateFilterOption = dateFiltersForResourceType.find(
    _ => _.property === configuredDateFilterProperty,
  );

  return configuredDateFilterOption || defaultDateFilter;
};

const timePointTypes = {
  periodStart: 'PERIOD_START',
  periodEnd: 'PERIOD_END',
  instance: 'NON_PERIOD',
};

const specialHeaderFieldTypes = {
  tagSearch: 'TAG_SEARCH',
};

const timelineTraceHeight = 0.05;
const overlayTimelineTraceHeight = 0.02;

export {
  dataSourceTypes,
  timelineTraceHeight,
  timePointTypes,
  specialHeaderFieldTypes,
  dateFilterProperties,
  overlayTimelineTraceHeight,
};
